"use client";

/**
 * The activation rail the solution pages show. The process studies stay on the concept
 * board (concept/groups/process.tsx).
 */

import { DATA_LABEL_CLS, DISPLAY } from "@/components/editorial";

export const ACTIVATION_STEPS = [
  {
    n: "01",
    title: "Connect priority systems",
    body: "We agree the question, connect the systems it needs and start with read-only access.",
    extra: "Days 1–5",
  },
  {
    n: "02",
    title: "Build and check your Twin",
    body: "Your Twin connects the records, defines the calculations and tests them against your data. Work that fails the checks is left out.",
    extra: "Days 6–10",
  },
  {
    n: "03",
    title: "Review the Twin, then switch it on",
    body: "Check the result against real business examples with your team. The release stays off until you switch it on.",
    extra: "End of week 2",
  },
] as const;

/* ================================================================= */
/*  PRC-002 · Activation timeline, horizontal                          */
/* ================================================================= */

/** Minor tick strip along the rail. A userSpaceOnUse pattern keeps the
 *  ticks at a fixed pitch however wide the rail renders. */
export function TickStrip({ id }: { id: string }) {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 hidden h-3 w-full text-[var(--theme-ink)] md:block"
    >
      <defs>
        <pattern id={id} width="24" height="12" patternUnits="userSpaceOnUse">
          <line
            x1="0.5"
            y1="0"
            x2="0.5"
            y2="6"
            stroke="currentColor"
            strokeOpacity="0.32"
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

/**
 * The activation's three phases on their ticked rail, alone: PRC-002 draws it
 * under its own heading, and the home's start band under the band's.
 */
export function ActivationRail({
  id,
  timing,
  className = "",
}: {
  /** The tick pattern's id, unique on the page. */
  id: string;
  /** Show each phase's time box (Days 1–5 / Days 6–10 / End of week 2). */
  timing: boolean;
  className?: string;
}) {
  return (
    // The rail is divided into three equal phases, not a day scale; the
    // per-phase timing labels carry the calendar so the geometry makes no
    // claim it cannot keep. Tick decoration lives on this wrapper so the
    // <ol> holds only <li>s.
    <div className={`relative ${className}`}>
      <TickStrip id={id} />
      {/* Terminal tick — closes the rail at the end of week 2. */}
      <span
        aria-hidden="true"
        className="absolute top-0 right-0 hidden h-5 w-px bg-[var(--theme-ink)] md:block"
      />
      <ol className="relative grid grid-cols-3 border-t border-[var(--rule)] max-md:grid-cols-1 max-md:border-t-0 max-md:border-l max-md:pl-6">
        {ACTIVATION_STEPS.map((step, index) => (
          <li
            key={step.n}
            className={`relative pt-9 pb-2 md:pr-10 ${
              index > 0 ? "max-md:border-t max-md:border-dotted max-md:border-[var(--rule)]" : ""
            } max-md:pt-6 max-md:pb-6`}
          >
            {/* Major tick at the phase start (vertical on the horizontal
                rail; a dot against the left rail on mobile). */}
            <span
              aria-hidden="true"
              className="absolute top-0 left-0 hidden h-5 w-px bg-[var(--theme-ink)] md:block"
            />
            <span
              aria-hidden="true"
              className="absolute top-7 -left-[calc(1.5rem_+_4.5px)] size-2 bg-[var(--theme-dark)] md:hidden"
            />
            {timing ? (
              <p className={`text-[var(--theme-accent-text)] ${DATA_LABEL_CLS}`}>
                {step.extra}
              </p>
            ) : null}
            <h3
              className="mt-3 text-[1.15rem] leading-[1.3] font-semibold tracking-[-0.01em] text-[var(--theme-ink)] md:text-[1.25rem]"
              style={DISPLAY}
            >
              {step.title}
            </h3>
            <p className="mt-2 max-w-[30rem] text-[0.98rem] leading-[1.6] text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]">
              {step.body}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
